import React, { useState } from 'react';
import { array, arrayOf, bool, func, shape, string, oneOf, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  propTypes,
} from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
} from '../../util/urlHelpers';
import { convertMoneyToNumber } from '../../util/currency';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import { getCurrentLocale } from '../../util/locale';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

import {
  H4,
  Page,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  Heading,
  ListingStatus,
  IconArrowHead,
  SecondaryButtonInline,
  H1,
  IconVerified,
} from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
  offerListing,
  updateListingData,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
  handleOfferListing,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import SectionGallery from './SectionGallery';
import {
  canShowListingForUserLocale,
  getListingFlag,
  getNumberOfBuyingRequests,
  hasAnyBuyingRequest,
  isEndingSoon,
  isExpired,
  isListing2ndLife,
  isListingContract,
  isListingRespare,
  resolveDraftListingTitle,
  shouldShowContractDetails,
  shouldShowCustomerManagement,
  shouldShowRefurbishDetails,
  shouldShowReviewDetails,
} from '../../util/listing';
import CommentsAndReviews from './CommentsAndReviews/CommentsAndReviews';
import OfferListingModal from './OfferListingModal';
import { LISTING_INTENT_TYPE } from '../../constants/listing';
import { createResourceLocatorString } from '../../util/routes';
import { isMarketplaceRespare, isMarketplaceSecondLife } from '../../util/marketplace';
import SectionContractDetails from './SectionContractDetails';
import SectionContractProductDetails from './SectionContractProductDetails';
import SectionContractReviewDetails from './SectionContractReviewDetails';
import SectionContractRefurbishDetails from './SectionContractRefurbishDetails';
import SectionContractStatusHistory from './SectionContractStatusHistory';
import { isListingPermissionDeniedError } from '../../util/errors';
import SectionCustomerManagement from './SectionCustomerManagement';
import SectionContactRequest from './SectionContactRequest/SectionContactRequest';
import { Tooltip } from 'react-tooltip';
import ListingFlag from '../../components/ListingFlag/ListingFlag';

import css from './ListingPage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;
const MODAL_BREAKPOINT = 768;

const { UUID } = sdkTypes;

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews,
    fetchReviewsError,
    fetchReviewsInProgress,
    sendInquiryInProgress,
    sendInquiryError,
    monthlyTimeSlots,
    onFetchTimeSlots,
    listingConfig: listingConfigProp,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    onOfferListing,
    offerListingInProgress,
    offerListingError,
    onUpdateListing,
  } = props;

  const isWindowDefined = typeof window !== 'undefined';
  const isMobileLayout = isWindowDefined && window.innerWidth < MODAL_BREAKPOINT;

  // prop override makes testing a bit easier
  // TODO: improve this when updating test setup
  const listingConfig = listingConfigProp || config.listing;
  const listingId = new UUID(rawParams.id);
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  currentListing.attributes.title = resolveDraftListingTitle(intl, currentListing.attributes.title);

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isListingContract(currentListing)
    ? 'contract-details'
    : isDraftVariant
    ? 'delivery'
    : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData: listingPublicData = {},
    metadata = {},
  } = currentListing.attributes;

  const { schemaImageUrls, dimrProductImageStudio, dimrGenericNameShort, ...publicData } =
    listingPublicData || {};

  const { locale, intentType } = publicData;

  const isRequesting = intentType === LISTING_INTENT_TYPE.REQUESTING;

  const isMarketplaceMatch =
    (isMarketplaceRespare(config) && isListingRespare(currentListing)) ||
    (isMarketplaceSecondLife(config) && isListing2ndLife(currentListing));

  const currentMarketplaceLocale = getCurrentLocale();
  const isLocaleMatch = isMarketplaceSecondLife(config)
    ? canShowListingForUserLocale(currentListing, currentUser)
    : currentMarketplaceLocale === locale;

  const showLoadingPage = !currentListing.id;
  const showNotFoundPage = showListingError && showListingError.status === 404;

  const topbar = <TopbarContainer />;

  if (showNotFoundPage) {
    // 404 listing not found
    return <NotFoundPage />;
  } else if (showListingError) {
    if (isListingPermissionDeniedError(showListingError) && !isAuthenticated) {
      return (
        <NamedRedirect
          name="LoginPage"
          state={{
            from: `${location.pathname}${location.search}${location.hash}`,
          }}
        />
      );
    }

    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (showLoadingPage) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!isMarketplaceMatch) {
    return <NotFoundPage />;
  } else if (currentMarketplaceLocale && locale && !isLocaleMatch) {
    return <NotFoundPage />;
  }

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const onOpenOfferListingModal = handleOfferListing({
    ...commonParams,
    currentUser,
    location,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };

  const onOfferListingSubmit = values => {
    const { initialMessage } = values;

    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;

    if (isOwnListing || isCurrentlyClosed) window.scrollTo(0, 0);
    else {
      onOfferListing(currentListing.id, initialMessage).then(txId => {
        // Redirect to OrderDetailsPage
        history.push(
          createResourceLocatorString('OrderDetailsPage', routeConfiguration, { id: txId.uuid }, {})
        );
      });
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const schemaPriceMaybe = price
    ? {
        price: intl.formatNumber(convertMoneyToNumber(price), {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        priceCurrency: price.currency,
      }
    : {};
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability =
    currentStock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';

  const createFilterOptions = options => options.map(o => ({ key: `${o.option}`, label: o.label }));

  const isListingExpired = isExpired(currentListing);
  const isListingEndingSoon = isEndingSoon(currentListing);
  const hasBuyingRequests = hasAnyBuyingRequest(currentListing);
  const noOfBuyingRequests = hasBuyingRequests
    ? getNumberOfBuyingRequests(currentListing)
    : undefined;

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...schemaPriceMaybe,
          availability: schemaAvailability,
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.contentWrapperForProductLayout}>
          <div className={css.mainColumnForProductLayout}>
            {currentListing.id ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                editParams={{
                  id: listingId.uuid,
                  slug: listingSlug,
                  type: listingPathParamType,
                  tab: listingTab,
                }}
              />
            ) : null}
            {isMarketplaceSecondLife(config) && (
              <SecondaryButtonInline
                type="button"
                onClick={() => {
                  const { from } = location.state || {};

                  if (from) history.push(from);
                  else history.push(createResourceLocatorString('LandingPage', routeConfiguration));
                }}
                className={css.goBack}
              >
                <IconArrowHead direction="left" /> {intl.formatMessage({ id: 'General.goBack' })}
              </SecondaryButtonInline>
            )}
            {isMarketplaceSecondLife(config) && (
              <div className={css.listingGallery}>
                {(isListingExpired || isListingEndingSoon || hasBuyingRequests) && (
                  <ListingFlag
                    position="bottomLeft"
                    flag={
                      hasBuyingRequests
                        ? noOfBuyingRequests > 1
                          ? 'buyingRequests'
                          : 'buyingRequest'
                        : getListingFlag(currentListing)
                    }
                    intl={intl}
                    numberOfBuyingRequests={noOfBuyingRequests}
                    className={css.leaseFlag}
                  />
                )}
                {isAuthenticated && (
                  <ListingStatus
                    listing={currentListing}
                    className={
                      isListingExpired || isListingEndingSoon || hasBuyingRequests
                        ? css.leaseStatusWithFlag
                        : css.leaseStatus
                    }
                    position={
                      isListingExpired || isListingEndingSoon || hasBuyingRequests
                        ? 'topLeft'
                        : 'bottomLeft'
                    }
                  />
                )}
                {publicData?.husqvarnaCertified && (
                  <span
                    className={isAuthenticated ? css.certifiedFlag : css.certifiedFlagLeft}
                    data-tooltip-id="my-tooltip"
                  >
                    <IconVerified />
                    <Tooltip
                      id="my-tooltip"
                      place="top-center"
                      content={intl.formatMessage({ id: 'General.husqvarnaCertified' })}
                      className={css.tooltip}
                      clickable
                    />
                  </span>
                )}
                <SectionGallery
                  listing={currentListing}
                  variantPrefix={config.layout.listingImage.variantPrefix}
                />
              </div>
            )}
            {isMarketplaceRespare(config) && (
              <SectionGallery
                listing={currentListing}
                variantPrefix={config.layout.listingImage.variantPrefix}
              />
            )}
            <div className={css.mobileHeading}>
              <H4 as="h1" className={css.orderPanelTitle}>
                {isAuthenticated ? (
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                ) : (
                  publicData?.dimrModel
                )}
              </H4>
            </div>

            {isRequesting ? (
              <Heading as="h2" rootClassName={css.sectionHeading} className={css.sectionDetails}>
                <FormattedMessage id="General.quantity" />
                {`: ${currentStock}`}
              </Heading>
            ) : null}
            {isMarketplaceSecondLife(config) && isAuthenticated && (
              <SectionContractStatusHistory listing={currentListing} currentUser={currentUser} />
            )}
            {isMarketplaceSecondLife(config) &&
              isAuthenticated &&
              shouldShowCustomerManagement(currentListing) && (
                <SectionCustomerManagement listing={currentListing} />
              )}
            {isMarketplaceSecondLife(config) &&
              isAuthenticated &&
              shouldShowContractDetails(currentListing) && (
                <SectionContractDetails listing={currentListing} />
              )}
            {isMarketplaceSecondLife(config) &&
              isAuthenticated &&
              shouldShowRefurbishDetails(currentListing) && (
                <SectionContractRefurbishDetails listing={currentListing} />
              )}
            {isMarketplaceSecondLife(config) &&
              isAuthenticated &&
              shouldShowReviewDetails(currentListing) && (
                <SectionContractReviewDetails listing={currentListing} />
              )}
            {isMarketplaceSecondLife(config) && (
              <SectionContractProductDetails
                listing={currentListing}
                isPublicView={!isAuthenticated}
              />
            )}

            {isMarketplaceRespare(config) && (
              <>
                <SectionTextMaybe text={description} showAsIngress />
                <SectionDetailsMaybe
                  publicData={publicData}
                  metadata={metadata}
                  listingConfig={listingConfig}
                  intl={intl}
                />
              </>
            )}
            {isMarketplaceRespare(config) &&
              listingConfig.listingFields.reduce((pickedElements, config) => {
                const { key, enumOptions, scope = 'public' } = config;
                const value =
                  scope === 'public'
                    ? publicData[key]
                    : scope === 'metadata'
                    ? metadata[key]
                    : null;
                const hasValue = value !== null;

                const isEmptyEnumOptions = Array.isArray(enumOptions) && enumOptions?.length === 0;

                return hasValue && config.schemaType === SCHEMA_TYPE_MULTI_ENUM
                  ? [
                      ...pickedElements,
                      <SectionMultiEnumMaybe
                        key={key}
                        heading={config?.showConfig?.label}
                        options={createFilterOptions(
                          isEmptyEnumOptions
                            ? Array.isArray(value)
                              ? value.map(val => ({ option: val, label: val }))
                              : []
                            : enumOptions
                        )}
                        selectedOptions={value}
                      />,
                    ]
                  : hasValue && config.schemaType === SCHEMA_TYPE_TEXT
                  ? [
                      ...pickedElements,
                      <SectionTextMaybe
                        key={key}
                        heading={config?.showConfig?.label}
                        text={value}
                      />,
                    ]
                  : pickedElements;
              }, [])}

            {isMarketplaceRespare(config) && (
              <SectionMapMaybe
                geolocation={geolocation}
                publicData={publicData}
                listingId={currentListing.id}
                mapsConfig={config.maps}
              />
            )}
            {!isMarketplaceSecondLife(config) && (
              <SectionAuthorMaybe
                title={title}
                listing={currentListing}
                authorDisplayName={authorDisplayName}
                onContactUser={onContactUser}
                isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
                onCloseInquiryModal={() => setInquiryModalOpen(false)}
                sendInquiryError={sendInquiryError}
                sendInquiryInProgress={sendInquiryInProgress}
                onSubmitInquiry={onSubmitInquiry}
                currentUser={currentUser}
                onManageDisableScrolling={onManageDisableScrolling}
                isRequesting={isRequesting}
              />
            )}
            {isMarketplaceRespare(config) && (
              <CommentsAndReviews
                listingId={listingId.uuid}
                isOwnListing={isOwnListing}
                areCommentsAllowed={publicData?.areCommentsAllowed}
                isListingClosed={currentListing?.attributes?.state === LISTING_STATE_CLOSED}
                reviews={reviews}
                fetchReviewsError={fetchReviewsError}
                fetchReviewsInProgress={fetchReviewsInProgress}
              />
            )}
          </div>
          <div className={css.orderColumnForProductLayout}>
            <OrderPanel
              className={css.productOrderPanel}
              modalClassName={css.orderPanelModal}
              listing={currentListing}
              isOwnListing={isOwnListing}
              onSubmit={handleOrderSubmit}
              title={<FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />}
              titleDesktop={
                <H1 className={css.orderPanelTitle}>
                  {isAuthenticated ? (
                    <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                  ) : (
                    publicData?.dimrModel
                  )}
                </H1>
              }
              author={ensuredAuthor}
              onManageDisableScrolling={onManageDisableScrolling}
              onContactUser={onContactUser}
              monthlyTimeSlots={monthlyTimeSlots}
              onFetchTimeSlots={onFetchTimeSlots}
              onFetchTransactionLineItems={onFetchTransactionLineItems}
              lineItems={lineItems}
              fetchLineItemsInProgress={fetchLineItemsInProgress}
              fetchLineItemsError={fetchLineItemsError}
              marketplaceCurrency={config.currency}
              dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
              marketplaceName={config.marketplaceName}
              onOfferListing={onOpenOfferListingModal}
              // 2ndlife prop
              handleUpdatedListing={updatedListing => onUpdateListing(updatedListing, config)}
              isAuthenticated={isAuthenticated}
              contactOwner={
                isMarketplaceSecondLife(config) &&
                !isAuthenticated && (
                  <SectionContactRequest
                    listingId={currentListing.id.uuid}
                    onManageDisableScrolling={onManageDisableScrolling}
                  />
                )
              }
            />

            <OfferListingModal
              onManageDisableScrolling={onManageDisableScrolling}
              onSubmit={onOfferListingSubmit}
              authorDisplayName={authorDisplayName}
              title={richTitle}
              offerListingInProgress={offerListingInProgress}
              offerListingError={offerListingError}
            />
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

ListingPageComponent.defaultProps = {
  currentUser: null,
  inquiryModalOpenForListingId: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: null,
  sendInquiryError: null,
  listingConfig: null,
  lineItems: null,
  fetchLineItemsError: null,
};

ListingPageComponent.propTypes = {
  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,
  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT]),
  }).isRequired,

  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  inquiryModalOpenForListingId: string,
  showListingError: propTypes.error,
  callSetInitialValues: func.isRequired,
  reviews: arrayOf(propTypes.review),
  fetchReviewsError: propTypes.error,
  monthlyTimeSlots: object,
  // monthlyTimeSlots could be something like:
  // monthlyTimeSlots: {
  //   '2019-11': {
  //     timeSlots: [],
  //     fetchTimeSlotsInProgress: false,
  //     fetchTimeSlotsError: null,
  //   }
  // }
  sendInquiryInProgress: bool.isRequired,
  sendInquiryError: propTypes.error,
  onSendInquiry: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
  listingConfig: object,
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
};

const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    fetchReviewsInProgress,
    monthlyTimeSlots,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,

    offerListingInProgress,
    offerListingError,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    fetchReviewsInProgress,
    monthlyTimeSlots,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,

    offerListingInProgress,
    offerListingError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)),
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone)),
  onOfferListing: (listingId, message) => dispatch(offerListing(listingId, message)),

  onUpdateListing: (newListing, config) => dispatch(updateListingData(newListing, config)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedListingPage);

export default ListingPage;
